import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Reusable image lightbox with:
 *  - Centered preview (vertically + horizontally)
 *  - Prev / Next arrows (clickable + keyboard ArrowLeft / ArrowRight)
 *  - Swipe left / right on touch devices
 *  - Esc to close
 *  - Click background to close (but NOT clicking on image / controls)
 *
 * Props:
 *  - images: string[]  – array of image URLs
 *  - index: number     – currently displayed index
 *  - onClose: () => void
 *  - onChange: (newIndex: number) => void
 */
const Lightbox = ({ images, index, onClose, onChange }) => {
  const { tx } = useLanguage();
  const total = images.length;
  const touchStartX = useRef(null);
  const [activeArrow, setActiveArrow] = useState(null);

  const goPrev = () => {
    setActiveArrow("left");
    onChange((index - 1 + total) % total);
    setTimeout(() => setActiveArrow(null), 200);
  };
  const goNext = () => {
    setActiveArrow("right");
    onChange((index + 1) % total);
    setTimeout(() => setActiveArrow(null), 200);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    // lock body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.92)" }}
      onClick={onClose}
      data-testid="lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
        aria-label={tx("Mbylle")}
        data-testid="lightbox-close"
      >
        <i className="fas fa-times text-lg"></i>
      </button>

      {/* Counter */}
      <div className="absolute top-6 left-6 text-white/70 text-sm font-medium z-10" data-testid="lightbox-counter">
        {index + 1} / {total}
      </div>

      {/* Prev */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className={`absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full text-white flex items-center justify-center transition-all z-10 ${activeArrow === "left" ? "bg-[#1CA3B0]" : "bg-white/10 hover:bg-white/20"}`}
          aria-label={tx("Foto e meparshme")}
          data-testid="lightbox-prev"
        >
          <i className="fas fa-chevron-left text-base sm:text-lg"></i>
        </button>
      )}

      {/* Image — wraps in a flex container so it is centered both axes */}
      <div
        className="w-full h-full flex items-center justify-center p-12 sm:p-16"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          key={index}
          src={images[index]}
          alt=""
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain rounded-xl shadow-2xl select-none"
          style={{ display: "block", margin: "0 auto" }}
          data-testid="lightbox-image"
          draggable={false}
        />
      </div>

      {/* Next */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className={`absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full text-white flex items-center justify-center transition-all z-10 ${activeArrow === "right" ? "bg-[#1CA3B0]" : "bg-white/10 hover:bg-white/20"}`}
          aria-label={tx("Foto tjeter")}
          data-testid="lightbox-next"
        >
          <i className="fas fa-chevron-right text-base sm:text-lg"></i>
        </button>
      )}
    </div>
  );
};

export default Lightbox;
